import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, LogOut, FileText, Pencil, Trash2, Eye } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Failed to delete post");
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="border-b border-line bg-bone-surface">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
            <span className="font-serif text-2xl text-forest">Shelton Law</span>
            <span className="text-xs uppercase tracking-[0.2em] text-gold-deep border-l border-line pl-3">Admin</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.2em] font-semibold">
            <Link to="/admin" className="text-forest border-b border-gold pb-1" data-testid="admin-nav-posts">Posts</Link>
            <Link to="/admin/resources" className="text-ink-soft hover:text-forest" data-testid="admin-nav-resources">Resources</Link>
            <Link to="/admin/ccl/changes" className="text-ink-soft hover:text-forest" data-testid="admin-nav-ccl">CCL Sync</Link>
          </nav>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-ink-soft hover:text-forest" data-testid="admin-view-site">View site</Link>
            <span className="text-ink-soft hidden md:inline" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 text-ink hover:text-forest"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="overline mb-2">Content Management</p>
            <h1 className="font-serif text-4xl text-forest tracking-tight">Blog posts</h1>
            <p className="text-ink-soft text-sm mt-2">{posts.length} {posts.length === 1 ? "post" : "posts"}</p>
          </div>
          <Button
            onClick={() => navigate("/admin/posts/new")}
            className="bg-forest text-bone hover:bg-forest-light rounded-none px-6 py-5"
            data-testid="admin-new-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New post
          </Button>
        </div>

        {loading && <p className="text-ink-soft" data-testid="admin-posts-loading">Loading…</p>}

        {!loading && posts.length === 0 && (
          <div className="border border-line p-16 text-center" data-testid="admin-no-posts">
            <FileText className="w-10 h-10 mx-auto text-ink-soft mb-4" strokeWidth={1.5} />
            <p className="font-serif text-2xl text-forest mb-2">No posts yet.</p>
            <p className="text-ink-soft text-sm mb-6">Write your first article to get started.</p>
            <Button onClick={() => navigate("/admin/posts/new")} className="bg-forest text-bone hover:bg-forest-light rounded-none">
              Create the first post
            </Button>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="border border-line divide-y divide-line">
            {posts.map((p) => (
              <div key={p.id} className="p-6 flex flex-col md:flex-row md:items-center gap-4 bg-bone hover:bg-bone-alt transition-colors" data-testid={`admin-post-row-${p.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <Badge variant={p.published ? "default" : "outline"} className={p.published ? "bg-forest text-bone rounded-none" : "border-line text-ink-soft rounded-none"} data-testid={`admin-post-status-${p.slug}`}>
                      {p.published ? "Published" : "Draft"}
                    </Badge>
                    <span className="text-xs uppercase tracking-[0.18em] text-gold-deep">{p.category}</span>
                  </div>
                  <h3 className="font-serif text-xl text-forest leading-tight">{p.title}</h3>
                  <p className="text-xs text-ink-soft mt-1">/{p.slug}</p>
                </div>
                <div className="flex items-center gap-2">
                  {p.published && (
                    <Link
                      to={`/blog/${p.slug}`}
                      className="p-2 text-ink-soft hover:text-forest"
                      target="_blank"
                      data-testid={`admin-view-${p.slug}`}
                      title="View public post"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                  )}
                  <Link
                    to={`/admin/posts/${p.id}/edit`}
                    className="p-2 text-ink-soft hover:text-forest"
                    data-testid={`admin-edit-${p.slug}`}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDelete(p.id, p.title)}
                    className="p-2 text-ink-soft hover:text-destructive"
                    data-testid={`admin-delete-${p.slug}`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
